// İzin Tanımları ve Kontrol Fonksiyonları
import { useSelector } from 'react-redux';

// Tüm izin tanımları
export const PERMISSIONS = {
  // Dashboard İzinleri
  DASHBOARD_VIEW: 'dashboard.view',
  DASHBOARD_MANAGE: 'dashboard.manage',
  
  // Bildirim İzinleri
  NOTIFICATIONS_READ: 'notifications.read',
  NOTIFICATIONS_MANAGE: 'notifications.manage',
  
  // Kullanıcı Yönetimi İzinleri
  USERS_READ: 'users.read',
  USERS_MANAGE: 'users.manage',
  
  // Rol Yönetimi İzinleri
  ROLES_READ: 'roles.read',
  ROLES_MANAGE: 'roles.manage',
  
  // Kampanya Yönetimi İzinleri
  CAMPAIGNS_READ: 'campaigns.read',
  CAMPAIGNS_MANAGE: 'campaigns.manage',

  // Ödeme İzinleri
  PAYMENTS_VIEW: 'payments.view',
  PAYMENTS_MANAGE: 'payments.manage',
};

export const PERMISSION_DENIED_MESSAGE = 'Bu işlem için yetkiniz yok.';

export const permissionGuardClass = (allowed) =>
  allowed ? '' : 'opacity-50 cursor-not-allowed';

// İzin açıklamaları
export const PERMISSION_DESCRIPTIONS = {
  [PERMISSIONS.DASHBOARD_VIEW]: 'Dashboard verilerini görüntüleme',
  [PERMISSIONS.DASHBOARD_MANAGE]: 'Dashboard ayarlarını yönetme',
  [PERMISSIONS.NOTIFICATIONS_READ]: 'Bildirimleri görüntüleme',
  [PERMISSIONS.NOTIFICATIONS_MANAGE]: 'Bildirim oluşturma ve düzenleme',
  [PERMISSIONS.USERS_READ]: 'Yönetici kullanıcılarını görüntüleme',
  [PERMISSIONS.USERS_MANAGE]: 'Yönetici kullanıcılarını oluşturma ve düzenleme',
  [PERMISSIONS.ROLES_READ]: 'Rolleri görüntüleme',
  [PERMISSIONS.ROLES_MANAGE]: 'Rolleri oluşturma, düzenleme ve silme',
  [PERMISSIONS.CAMPAIGNS_READ]: 'Kampanyaları görüntüleme',
  [PERMISSIONS.CAMPAIGNS_MANAGE]: 'Kampanyaları oluşturma ve düzenleme',
  [PERMISSIONS.PAYMENTS_VIEW]: 'Ödeme metriklerini görüntüleme',
  [PERMISSIONS.PAYMENTS_MANAGE]: 'Ödeme ayarlarını yönetme',
};

// İzin kategorileri
export const PERMISSION_CATEGORIES = {
  DASHBOARD: {
    name: 'Dashboard',
    permissions: [PERMISSIONS.DASHBOARD_VIEW, PERMISSIONS.DASHBOARD_MANAGE]
  },
  NOTIFICATIONS: {
    name: 'Bildirimler',
    permissions: [PERMISSIONS.NOTIFICATIONS_READ, PERMISSIONS.NOTIFICATIONS_MANAGE]
  },
  USERS: {
    name: 'Kullanıcı Yönetimi',
    permissions: [PERMISSIONS.USERS_READ, PERMISSIONS.USERS_MANAGE]
  },
  ROLES: {
    name: 'Rol Yönetimi',
    permissions: [PERMISSIONS.ROLES_READ, PERMISSIONS.ROLES_MANAGE]
  },
  CAMPAIGNS: {
    name: 'Kampanya Yönetimi',
    permissions: [PERMISSIONS.CAMPAIGNS_READ, PERMISSIONS.CAMPAIGNS_MANAGE]
  },
  PAYMENTS: {
    name: 'Ödeme Yönetimi',
    permissions: [PERMISSIONS.PAYMENTS_VIEW, PERMISSIONS.PAYMENTS_MANAGE]
  }
};

/**
 * Kullanıcının belirli bir izni olup olmadığını kontrol eder
 * @param {Object} user - Kullanıcı objesi (Redux'tan gelen)
 * @param {string} permission - Kontrol edilecek izin
 * @returns {boolean} - İzin var mı?
 */
export const hasPermission = (user, permission) => {
  if (!user) {
    return false;
  }
  
  // Geçici süper admin kontrolü - email bazlı
  if (user.email && (
    user.email.includes('admin') || 
    user.email.includes('super') ||
    user.email === 'admin@sadakat.com' ||
    user.email === 'umitsahin@yenikoza.com.tr' // Sizin email'iniz için özel
  )) {
    return true;
  }
  
  // Alternatif: fullName kontrolü
  if (user.fullName && (
    user.fullName.includes('ADMİN') ||
    user.fullName.includes('ADMIN') ||
    user.fullName.toLowerCase().includes('admin')
  )) {
    return true;
  }
  
  // Super admin kontrolü (eğer varsa)
  if (user.isSuperAdmin) {
    return true;
  }
  
  // User.permissions array'ini kontrol et (backend'den direkt geliyor)
  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions.includes(permission);
  }
  
  return false;
};

/**
 * Kullanıcının birden fazla izinden herhangi birine sahip olup olmadığını kontrol eder
 * @param {Object} user - Kullanıcı objesi
 * @param {Array} permissions - Kontrol edilecek izinler dizisi
 * @returns {boolean} - En az bir izin var mı?
 */
export const hasAnyPermission = (user, permissions) => {
  return permissions.some(permission => hasPermission(user, permission));
};

/**
 * Kullanıcının tüm belirtilen izinlere sahip olup olmadığını kontrol eder
 * @param {Object} user - Kullanıcı objesi
 * @param {Array} permissions - Kontrol edilecek izinler dizisi
 * @returns {boolean} - Tüm izinler var mı?
 */
export const hasAllPermissions = (user, permissions) => {
  return permissions.every(permission => hasPermission(user, permission));
};

/**
 * Kullanıcının sahip olduğu tüm izinleri döndürür
 * @param {Object} user - Kullanıcı objesi
 * @returns {Array} - Kullanıcının sahip olduğu izinler
 */
export const getUserPermissions = (user) => {
  if (!user) return [];
  
  // User.permissions array'ini döndür (backend'den direkt geliyor)
  if (user.permissions && Array.isArray(user.permissions)) {
    return user.permissions;
  }
  
  return [];
};

/**
 * İzin kontrolü için React Hook
 * @param {string|Array} permission - Kontrol edilecek izin(ler)
 * @param {string} mode - 'any' (herhangi biri) veya 'all' (hepsi)
 * @returns {boolean} - İzin durumu
 */
export const usePermissionHook = (permission, mode = 'any') => {
  const { user } = useSelector((state) => state.auth);
  
  if (!permission) return true;
  
  // Geçici süper admin kontrolü
  if (user && user.email && (
    user.email.includes('admin') || 
    user.email.includes('super') ||
    user.email === 'admin@sadakat.com' ||
    user.email === 'umitsahin@yenikoza.com.tr'
  )) {
    return true;
  }
  
  // Alternatif: fullName kontrolü
  if (user && user.fullName && (
    user.fullName.includes('ADMİN') ||
    user.fullName.includes('ADMIN') ||
    user.fullName.toLowerCase().includes('admin')
  )) {
    return true;
  }
  
  if (Array.isArray(permission)) {
    if (mode === 'all') {
      return hasAllPermissions(user, permission);
    } else {
      return hasAnyPermission(user, permission);
    }
  } else {
    return hasPermission(user, permission);
  }
};

// Backward compatibility için eski fonksiyon
export const usePermission = (permission, mode = 'any') => {
  return usePermissionHook(permission, mode);
};

// Sayfa bazlı izin gereksinimleri
export const PAGE_PERMISSIONS = {
  '/dashboard': [PERMISSIONS.DASHBOARD_VIEW],
  '/kullanicilar': [PERMISSIONS.USERS_READ],
  '/kampanyalar': [PERMISSIONS.CAMPAIGNS_READ],
  '/kampanyalar/bana-ozel': [PERMISSIONS.CAMPAIGNS_READ],
  '/kampanyalar/genel': [PERMISSIONS.CAMPAIGNS_READ],
  '/bildirimler': [PERMISSIONS.NOTIFICATIONS_READ],
  '/kullanici-rolleri': [PERMISSIONS.ROLES_READ],
  '/odemeler': [PERMISSIONS.PAYMENTS_VIEW],
};

// Buton/aksiyon bazlı izin gereksinimleri
export const ACTION_PERMISSIONS = {
  // Dashboard aksiyonları
  'dashboard.refresh': [PERMISSIONS.DASHBOARD_VIEW],
  'dashboard.settings': [PERMISSIONS.DASHBOARD_MANAGE],
  
  // Kullanıcı aksiyonları
  'users.create': [PERMISSIONS.USERS_MANAGE],
  'users.edit': [PERMISSIONS.USERS_MANAGE],
  'users.delete': [PERMISSIONS.USERS_MANAGE],
  'users.view_details': [PERMISSIONS.USERS_READ],
  
  // Kampanya aksiyonları
  'campaigns.create': [PERMISSIONS.CAMPAIGNS_MANAGE],
  'campaigns.edit': [PERMISSIONS.CAMPAIGNS_MANAGE],
  'campaigns.delete': [PERMISSIONS.CAMPAIGNS_MANAGE],
  'campaigns.view_details': [PERMISSIONS.CAMPAIGNS_READ],
  'campaigns.view_performance': [PERMISSIONS.CAMPAIGNS_READ],
  
  // Bildirim aksiyonları
  'notifications.create': [PERMISSIONS.NOTIFICATIONS_MANAGE],
  'notifications.edit': [PERMISSIONS.NOTIFICATIONS_MANAGE],
  'notifications.delete': [PERMISSIONS.NOTIFICATIONS_MANAGE],
  'notifications.send': [PERMISSIONS.NOTIFICATIONS_MANAGE],
  
  // Rol aksiyonları
  'roles.create': [PERMISSIONS.ROLES_MANAGE],
  'roles.edit': [PERMISSIONS.ROLES_MANAGE],
  'roles.delete': [PERMISSIONS.ROLES_MANAGE],
  'roles.assign': [PERMISSIONS.ROLES_MANAGE],
};

export default {
  PERMISSIONS,
  PERMISSION_DESCRIPTIONS,
  PERMISSION_CATEGORIES,
  PAGE_PERMISSIONS,
  ACTION_PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getUserPermissions,
  usePermission,
};